import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Check, Plus, Trash2, AlertCircle } from "lucide-react";
import confetti from "canvas-confetti";
import { Navbar } from "@/components/Navbar";
import { CommandPalette } from "@/components/CommandPalette";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { DirectSplitDialog } from "@/components/DirectSplitDialog";
import { api, currencySymbol, formatAmount, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useHistoryTab } from "@/hooks/useHistoryTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNowStrict } from "date-fns";

export default function FriendDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [amt, setAmt] = useState("");
  const [method, setMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useHistoryTab("overview");

  const load = async () => {
    try {
      const { data } = await api.get(`/friends/${id}/detail`);
      setData(data);
    } catch {
      toast.error("Friend not found");
      nav("/friends");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (settleOpen && data) {
      // Pre-fill with absolute net so user pays/receives to flatten the balance
      setAmt(Math.abs(data.net).toFixed(2));
      setMethod("cash");
      setNotes("");
    }
  }, [settleOpen, data]);

  const confirmSettle = async () => {
    const amount = Number(amt);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    const direction = data.net > 0 ? "received" : "paid";
    setSaving(true);
    try {
      await api.post(`/friends/${id}/settle`, { amount, direction, currency: data.currency || "USD", method, notes: notes || null });
      confetti({ particleCount: 80, spread: 55, origin: { y: 0.7 }, colors: ["#00FF66", "#FFFFFF"] });
      toast.success("Settled ✓");
      setSettleOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const delSplit = async (sid) => {
    try { await api.delete(`/direct-splits/${sid}`); load(); } catch { toast.error("Failed"); }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-10 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const f = data.friend;
  const currency = data.currency || "USD";
  const pos = data.net >= 0;
  const allPayments = [
    ...(data.direct_settlements || []).map((s) => ({ ...s, origin: "direct" })),
    ...(data.group_settlements || []).map((s) => ({ ...s, origin: "group" })),
  ].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
      <CommandPalette open={cmdkOpen} setOpen={setCmdkOpen} />
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <Link to="/friends" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="fd-back">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to friends
        </Link>

        {/* Header */}
        <div className="rounded-2xl border p-6 mb-6 relative overflow-hidden"
          style={{ borderColor: pos ? "rgba(0,255,102,0.3)" : "rgba(255,59,48,0.3)", background: pos ? "rgba(0,255,102,0.04)" : "rgba(255,59,48,0.04)" }}
          data-testid="fd-header">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <span className="h-14 w-14 rounded-full bg-foreground/5 border border-border flex items-center justify-center text-2xl shrink-0">{f.avatar_emoji || "🙂"}</span>
              <div className="min-w-0">
                <h1 className="font-heading text-3xl tracking-tight truncate" data-testid="fd-name">{f.name}</h1>
                <div className="text-xs font-mono text-muted-foreground truncate">{f.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setSplitOpen(true)} variant="outline" className="border-border bg-foreground/[0.02] hover:bg-foreground/[0.05] h-10" data-testid="fd-split-btn">
                <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Split
              </Button>
              {Math.abs(data.net) > 0.005 && (
                <Button onClick={() => setSettleOpen(true)} className="brand-bg text-black hover:opacity-90 h-10" data-testid="fd-settle-btn">
                  <Check className="h-4 w-4 mr-1.5" strokeWidth={2} /> Settle Up
                </Button>
              )}
            </div>
          </div>
          <div className="mt-6 flex items-end gap-3 flex-wrap">
            <AnimatedNumber value={data.net} currency={currency} withSign className={`font-mono text-4xl font-semibold ${pos ? "brand-text" : "text-red-400"}`} />
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground pb-1.5">
              {pos ? `${f.name} owes you` : `you owe ${f.name}`}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-card border border-border" data-testid="fd-tabs">
            <TabsTrigger value="overview" data-testid="fd-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="history" data-testid="fd-tab-history">History</TabsTrigger>
            <TabsTrigger value="splits" data-testid="fd-tab-splits">Direct Splits</TabsTrigger>
            <TabsTrigger value="payments" data-testid="fd-tab-payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="rounded-2xl border border-border bg-card/40 p-6 mt-4 space-y-5" data-testid="fd-overview">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">By group</div>
                {data.by_group.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-2">No group balances</div>
                ) : (
                  <div className="space-y-1.5">
                    {data.by_group.map((g) => (
                      <Link key={g.group_id} to={`/groups/${g.group_id}`} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-foreground/[0.03]" data-testid={`fd-group-${g.group_id}`}>
                        <span className="flex items-center gap-2 text-sm min-w-0"><span className="text-lg">{g.group_emoji}</span><span className="truncate">{g.group_name}</span></span>
                        <span className={`font-mono tabular-nums text-sm ${g.net >= 0 ? "brand-text" : "text-red-400"}`}>{g.net >= 0 ? "+" : "-"}{formatAmount(g.net, g.currency)}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="pt-4 border-t border-border grid grid-cols-2 gap-4">
                <Stat label="Groups net" value={data.net - data.direct_net - data.settle_net} currency={currency} />
                <Stat label="Direct splits net" value={data.direct_net} currency={currency} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="rounded-2xl border border-border bg-card/40 p-6 mt-4" data-testid="fd-history">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">All activity with {f.name}</div>
              {data.direct_splits.length + allPayments.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">No history yet</div>
              ) : (
                <div className="divide-y divide-border">
                  {[...data.direct_splits.map((s) => ({ type: "split", ...s })), ...allPayments.map((s) => ({ type: "payment", ...s }))]
                    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
                    .map((item) => {
                      if (item.type === "split") {
                        const cat = CATEGORIES.find((c) => c.value === item.category);
                        return (
                          <div key={`ds-${item.id}`} className="flex items-center justify-between py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="h-9 w-9 rounded-lg bg-foreground/5 flex items-center justify-center">{cat?.emoji || "💸"}</span>
                              <div className="min-w-0">
                                <div className="text-sm truncate">{item.title}</div>
                                <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Split · {item.paid_by === user.id ? "you paid" : `${f.name} paid`} · {formatDistanceToNowStrict(new Date(item.created_at), { addSuffix: true })}</div>
                              </div>
                            </div>
                            <span className="font-mono tabular-nums">{formatAmount(item.amount, item.currency || currency)}</span>
                          </div>
                        );
                      }
                      const incoming = item.to_user === user.id;
                      return (
                        <div key={`pay-${item.id}`} className="flex items-center justify-between py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`h-9 w-9 rounded-lg flex items-center justify-center text-xs ${incoming ? "bg-[var(--brand)]/15 brand-text" : "bg-foreground/5"}`}>
                              {incoming ? "→" : "←"}
                            </span>
                            <div className="min-w-0">
                              <div className="text-sm truncate">{incoming ? `${f.name} paid you` : `You paid ${f.name}`}</div>
                              <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                                {item.method || "cash"} · {item.origin === "group" && item.group_name ? `${item.group_emoji || ""} ${item.group_name} · ` : ""}{formatDistanceToNowStrict(new Date(item.created_at), { addSuffix: true })}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider brand-text">
                              <Check className="h-3 w-3" strokeWidth={2} /> SETTLED
                            </span>
                            <span className={`font-mono tabular-nums ${incoming ? "brand-text" : ""}`}>{incoming ? "+" : "-"}{formatAmount(item.amount, item.currency || currency)}</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="splits">
            <div className="rounded-2xl border border-border bg-card/40 p-6 mt-4" data-testid="fd-splits">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Direct splits</div>
                <Button onClick={() => setSplitOpen(true)} size="sm" className="brand-bg text-black h-8" data-testid="fd-splits-add"><Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Split</Button>
              </div>
              {data.direct_splits.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No direct splits yet</div>
              ) : (
                <div className="divide-y divide-border">
                  {data.direct_splits.map((s) => {
                    const cat = CATEGORIES.find((c) => c.value === s.category);
                    const iPaid = s.paid_by === user.id;
                    return (
                      <div key={s.id} className="flex items-center justify-between py-3" data-testid={`fd-split-row-${s.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="h-9 w-9 rounded-lg bg-foreground/5 flex items-center justify-center">{cat?.emoji || "💸"}</span>
                          <div className="min-w-0">
                            <div className="text-sm truncate">{s.title}</div>
                            <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider">{iPaid ? "You paid" : `${f.name} paid`} · {iPaid ? `they owe ${formatAmount(s.non_payer_share, s.currency || currency)}` : `you owe ${formatAmount(s.non_payer_share, s.currency || currency)}`}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono tabular-nums">{formatAmount(s.amount, s.currency || currency)}</span>
                          <button onClick={() => delSplit(s.id)} className="text-muted-foreground hover:text-red-400" data-testid={`fd-split-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="rounded-2xl border border-border bg-card/40 p-6 mt-4" data-testid="fd-payments">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Payments between you</div>
              {allPayments.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No payments logged</div>
              ) : (
                <div className="divide-y divide-border">
                  {allPayments.map((p) => {
                    const incoming = p.to_user === user.id;
                    return (
                      <div key={p.id} className="flex items-center justify-between py-3" data-testid={`fd-pay-row-${p.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${incoming ? "bg-[var(--brand)]/15 brand-text" : "bg-foreground/5"}`}>{incoming ? "→" : "←"}</span>
                          <div className="min-w-0">
                            <div className="text-sm truncate">{incoming ? `${f.name} paid you` : `You paid ${f.name}`}</div>
                            <div className="text-xs text-muted-foreground font-mono uppercase tracking-wider">{p.method || "cash"}{p.origin === "group" && p.group_name ? ` · ${p.group_emoji || ""} ${p.group_name}` : ""} · {formatDistanceToNowStrict(new Date(p.created_at), { addSuffix: true })}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider brand-text"><Check className="h-3 w-3" strokeWidth={2} /> SETTLED</span>
                          <span className={`font-mono tabular-nums ${incoming ? "brand-text" : ""}`}>{incoming ? "+" : "-"}{formatAmount(p.amount, p.currency || currency)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <DirectSplitDialog open={splitOpen} onOpenChange={setSplitOpen} friend={f} onSaved={() => { setSplitOpen(false); load(); }} currency={currency} />

      {/* Settle Up dialog */}
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="fd-settle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Settle up with {f.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border brand-border bg-[var(--brand)]/[0.08] p-4 text-center">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">
                {data.net > 0 ? `${f.name} pays you` : `You pay ${f.name}`}
              </div>
              <div className="text-xs font-mono text-muted-foreground">Current balance: {formatAmount(Math.abs(data.net), currency)}</div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Amount</Label>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-muted-foreground">{currencySymbol(currency)}</span>
                <Input type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} className="pl-8 bg-muted border-border h-11 font-mono" data-testid="fd-settle-amount" autoFocus />
              </div>
              {Number(amt) > Math.abs(data.net) && (
                <div className="mt-1.5 text-[11px] font-mono text-amber-400 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" strokeWidth={1.5} /> Overpaying — balance will flip
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Method</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                {["cash", "upi", "bank", "other"].map((m) => (
                  <button key={m} type="button" onClick={() => setMethod(m)}
                    className={`h-9 rounded-md border text-xs font-mono uppercase transition ${method === m ? "brand-border bg-[var(--brand)]/10 brand-text" : "border-border text-muted-foreground hover:text-foreground"}`}
                    data-testid={`fd-settle-method-${m}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 bg-muted border-border" data-testid="fd-settle-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={confirmSettle} disabled={saving} className="w-full brand-bg text-black hover:opacity-90 h-11" data-testid="fd-settle-confirm">
              {saving ? "Recording…" : `Record ${formatAmount(Number(amt || 0), currency)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, currency }) => (
  <div>
    <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
    <AnimatedNumber value={value} currency={currency} withSign className={`font-mono text-xl ${value >= 0 ? "brand-text" : "text-red-400"}`} />
  </div>
);
